import React, { createContext, useContext, useEffect, useRef, useState } from 'react'

import { getFromStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { useUser } from 'src/state/userState'

import websocketService from '../../services/webSocketService'

export type WebSocketContextType = {
    connected: boolean
    connect: () => void
    disconnect: () => void
    subscribe: (eventType: string, callback: (data: unknown) => void) => () => void
    send: (data: unknown) => void
}

export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined)

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [connected, setConnected] = useState(false)
    const { authenticated } = useUser()
    const lastTokenRef = useRef<string | null>(null)

    const checkConnection = () => {
        setConnected(websocketService.isConnected())
    }

    useEffect(() => {
        if (authenticated) {
            const userData = getFromStorage('user')
            if (userData?.accessToken) {
                lastTokenRef.current = userData.accessToken
                websocketService.setAuthToken(userData.accessToken)
                websocketService.connect()
            }
        } else {
            websocketService.disconnect()
        }

        const interval = setInterval(checkConnection, 1000)

        return () => {
            clearInterval(interval)
            if (!authenticated) {
                websocketService.disconnect()
            }
        }
    }, [authenticated])

    useEffect(() => {
        // Set up a polling interval to check if the token has changed
        const tokenCheckInterval = setInterval(() => {
            const userData = getFromStorage('user')
            const currentToken = userData?.accessToken

            if (currentToken && currentToken !== lastTokenRef.current) {
                lastTokenRef.current = currentToken
                websocketService.setAuthToken(currentToken)
            }
        }, 10000) // Check every 10 seconds

        return () => {
            clearInterval(tokenCheckInterval)
        }
    }, [])

    // Laptop back from sleep, tab visible again or network back: restore a lost connection right away
    useEffect(() => {
        const onWakeUp = () => {
            if (document.visibilityState === 'visible') websocketService.ensureConnected()
        }

        document.addEventListener('visibilitychange', onWakeUp)
        window.addEventListener('online', onWakeUp)
        window.addEventListener('focus', onWakeUp)

        return () => {
            document.removeEventListener('visibilitychange', onWakeUp)
            window.removeEventListener('online', onWakeUp)
            window.removeEventListener('focus', onWakeUp)
        }
    }, [])

    const contextValue: WebSocketContextType = {
        connected,
        connect: () => {
            const userData = getFromStorage('user')
            if (userData?.accessToken) {
                websocketService.setAuthToken(userData.accessToken)
                websocketService.connect()
            }
        },
        disconnect: () => {
            websocketService.disconnect()
        },
        subscribe: (eventType, callback) => {
            websocketService.addEventListener(eventType, callback)
            return () => websocketService.removeEventListener(eventType, callback)
        },
        send: (data) => websocketService.send(data),
    }

    return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>
}

export const useWebSocketContext = (): WebSocketContextType => {
    const context = useContext(WebSocketContext)
    if (context === undefined) {
        throw new Error('useWebSocketContext must be used within a WebSocketProvider')
    }
    return context
}

export default WebSocketProvider
